
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect } from "react";
import { Check, AlertCircle, Clock, CheckCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RecentActivityTable } from "@/components/dashboard/RecentActivityTable";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  fetchDashboardData,
  fetchRecentActivity,
  type DashboardMetrics,
  type RecentActivity,
  type OnboardingProgress 
} from "@/services/dashboardService";

export default function DashboardPage() {
  const { tenantId } = useAuth();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [onboardingProgress, setOnboardingProgress] = useState<OnboardingProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshingActivity, setIsRefreshingActivity] = useState(false);

  const isOnboardingMode = onboardingProgress && !onboardingProgress.hasEvents;

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setIsLoading(true);
        
        // Use the optimized RPC function to fetch both metrics and onboarding progress
        const { metrics: metricsData, onboardingProgress: progress } = await fetchDashboardData(tenantId);
        setMetrics(metricsData);
        setOnboardingProgress(progress);
        
        // Fetch recent activity for operational mode
        if (progress.hasEvents) {
          const activityData = await fetchRecentActivity(tenantId);
          setRecentActivity(activityData);
        }
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, [tenantId]);

  const handleRefreshActivity = async () => {
    try {
      setIsRefreshingActivity(true);
      const activityData = await fetchRecentActivity(tenantId);
      setRecentActivity(activityData);
    } catch (error) {
      console.error("Error refreshing recent activity:", error);
    } finally {
      setIsRefreshingActivity(false);
    }
  };


  if (isLoading) {
    return (
      <>
        <div>
          <h1 className="text-3xl font-bold tracking-tight mb-2">Dashboard</h1>
          <p className="text-muted-foreground mb-6">
            Loading your email automation overview...
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-lg border bg-card p-6 shadow-sm animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-3"></div>
              <div className="h-8 bg-muted rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-muted rounded w-2/3"></div>
            </div>
          ))}
        </div>
      </>
    );
  }

  if (isOnboardingMode) {
    return (
      <>
        <div>
          <h1 className="text-3xl font-bold tracking-tight mb-2">Welcome to AutoReply365</h1>
          <p className="text-muted-foreground mb-6">
            Complete these steps to set up your email automation system
          </p>
        </div>
        
        <div className="rounded-lg border p-6 shadow-sm">
          <h2 className="text-xl font-semibold mb-4">Setup Progress</h2>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 rounded-lg border">
              {onboardingProgress?.hasMailboxes ? (
                <Check className="h-5 w-5 text-green-600" />
              ) : (
                <div className="h-5 w-5 rounded-full border-2 border-muted-foreground" />
              )}
              <div className="flex-1">
                <h3 className="font-medium">Connect Mailboxes</h3>
                <p className="text-sm text-muted-foreground">
                  Add your email mailboxes to start automating replies
                </p>
              </div>
              {onboardingProgress?.hasMailboxes ? (
                <Badge variant="outline" className="text-green-600 border-green-600">
                  Complete
                </Badge>
              ) : (
                <Button asChild size="sm">
                  <Link to="/mailboxes">Get Started</Link>
                </Button>
              )}
            </div>
            
            <div className="flex items-center gap-3 p-4 rounded-lg border">
              {onboardingProgress?.hasTemplates ? (
                <Check className="h-5 w-5 text-green-600" />
              ) : (
                <div className="h-5 w-5 rounded-full border-2 border-muted-foreground" />
              )}
              <div className="flex-1">
                <h3 className="font-medium">Create Templates</h3>
                <p className="text-sm text-muted-foreground">
                  Design reply templates with dynamic variables
                </p>
              </div>
              {onboardingProgress?.hasTemplates ? (
                <Badge variant="outline" className="text-green-600 border-green-600">
                  Complete
                </Badge>
              ) : (
                <Button asChild size="sm">
                  <Link to="/templates">Get Started</Link>
                </Button>
              )}
            </div>
            
            <div className="flex items-center gap-3 p-4 rounded-lg border">
              {onboardingProgress?.hasRules ? (
                <Check className="h-5 w-5 text-green-600" />
              ) : (
                <div className="h-5 w-5 rounded-full border-2 border-muted-foreground" />
              )}
              <div className="flex-1">
                <h3 className="font-medium">Set Up Rules</h3>
                <p className="text-sm text-muted-foreground">
                  Define conditions and actions for automated replies
                </p>
              </div>
              {onboardingProgress?.hasRules ? (
                <Badge variant="outline" className="text-green-600 border-green-600">
                  Complete
                </Badge>
              ) : (
                <Button asChild size="sm">
                  <Link to="/mailboxes">Get Started</Link>
                </Button>
              )}
            </div>
            
            <div className="flex items-center gap-3 p-4 rounded-lg border">
              <div className="h-5 w-5 rounded-full border-2 border-muted-foreground" />
              <div className="flex-1">
                <h3 className="font-medium">Basic setup completed. Waiting for message events...</h3>
                <p className="text-sm text-muted-foreground">
                  Your automation is ready. We're waiting for incoming emails to process.
                </p>
              </div>
            </div>
          </div>
          
          {metrics && (
            <div className="mt-6 pt-6 border-t">
              <h3 className="font-medium mb-3">Quick Stats</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">{metrics.totalMailboxes}</p>
                  <p className="text-sm text-muted-foreground">Mailboxes</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{metrics.totalTemplates}</p>
                  <p className="text-sm text-muted-foreground">Templates</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{metrics.activeRules}</p>
                  <p className="text-sm text-muted-foreground">Active Rules</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  // Operational Mode
  return (
    <>
      <div>
        <h1 className="text-3xl font-bold tracking-tight mb-2">Dashboard</h1>
        <p className="text-muted-foreground mb-6">
          Overview of your email automation system
        </p>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-6">
        <Link to="/analytics">
          <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Recent Replies</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics?.recentEventsCount || 0}</div>
              <p className="text-xs text-muted-foreground">Last 7 days</p>
            </CardContent>
          </Card>
        </Link>
        
        <Link to="/mailboxes">
          <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Mailboxes</CardTitle>
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {metrics?.activeMailboxes || 0} / {metrics?.totalMailboxes || 0}
              </div>
              <p className="text-xs text-muted-foreground">Currently active</p>
            </CardContent>
          </Card>
        </Link>
        
        <Link to="/mailboxes">
          <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Rules</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {metrics?.activeRules || 0} / {metrics?.totalRules || 0}
              </div>
              <p className="text-xs text-muted-foreground">Currently active</p>
            </CardContent>
          </Card>
        </Link>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>
            <Link to="/analytics" className="hover:text-primary transition-colors">
              Recent Activity
            </Link>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RecentActivityTable
            data={recentActivity}
            isLoading={isRefreshingActivity}
            onRefresh={handleRefreshActivity}
          />
        </CardContent>
      </Card>
    </>
  );
}
